/**
 * Pledge Audit Trail
 *
 * Audit log view scoped to pledge lifecycle events. Pins resourceType=pledge
 * in every server request so only pledge-related audit entries are loaded.
 *
 * Every page of the tenant's pledge events is fetched (in batches of the
 * spec's maximum limit), so the grid's pagination, the actor/action filters,
 * free-text search and the pledge-event counts all run over the complete set.
 * The page previously fetched only the first 25 events and discarded the
 * server total, so older events were unreachable while the footer still read
 * "Page 1 of 1".
 *
 * Backend: GET /api/admin/audit/logs?resourceType=pledge (platform-admin-api port 9302)
 * Mount:   services/platform-admin-api/src/routes/audit.routes.ts
 * Spec:    audit-trail.openapi.yaml
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Download,
  Clock,
  AlertTriangle,
  RefreshCw,
  History,
} from 'lucide-react';
import {
  Badge,
  Button,
  DataGrid,
  FilterBar,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
  Skeleton,
  formatCalendarDate,
  formatDateTime,
} from '@aaimplatform/ui';
import type {
  BadgeProps,
  ColumnDef,
  FilterDefinition,
  ActiveFilter,
} from '@aaimplatform/ui';
import {
  auditApi,
  type AuditLogEntry,
  type AuditStatistics,
  type AuditLogFilterParams,
} from '../../api/services';
import type { components as AuditTrailComponents } from '@aaimplatform/institutional-types/audit-trail';

type AuditAction = AuditTrailComponents['schemas']['AuditLogEntryResponse']['action'];

/** Rows per grid page. */
const PAGE_SIZE = 25;

/** GET /audit/logs `limit` maximum (audit-trail.openapi.yaml). */
const FETCH_BATCH = 1000;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The server query every read and the export share. */
const PLEDGE_QUERY: AuditLogFilterParams = {
  resourceType: 'pledge',
  sortBy: 'timestamp',
  sortOrder: 'desc',
};

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

const ACTION_LABELS: Partial<Record<AuditAction, string>> = {
  PLEDGE_ACKNOWLEDGED: 'Pledge acknowledged',
  PLEDGE_ACCEPTED: 'Pledge accepted',
  PLEDGE_DECLINED: 'Pledge declined',
  PLEDGE_RETURNED: 'Pledge returned',
  PLEDGE_RELEASED: 'Pledge released',
  PLEDGE_EXPIRED: 'Pledge expired',
  PLEDGE_REASSIGNED: 'Pledge reassigned',
  DATA_EXPORT: 'Data export',
};

/**
 * Badge tone by lifecycle stage. Neutral by default: an audit action is a
 * record, not an alert, so none of them take the red accent.
 */
const ACTION_VARIANTS: Partial<Record<AuditAction, BadgeProps['variant']>> = {
  PLEDGE_ACCEPTED: 'success',
  PLEDGE_DECLINED: 'warning',
  PLEDGE_RETURNED: 'warning',
};

/** Tokens that read as acronyms or the brand when a code is humanized. */
const TOKEN_CASING: Record<string, string> = {
  fi: 'FI',
  e2e: 'E2E',
  aaim: 'Aaim',
  csv: 'CSV',
  id: 'ID',
  ip: 'IP',
  json: 'JSON',
  pdf: 'PDF',
  sftp: 'SFTP',
};

/** "PLATFORM_ADMIN" or "rowCount" -> "Platform admin" / "Row count". */
function humanizeCode(code: string): string {
  const words = code
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.toLowerCase());
  return words
    .map((w, i) => TOKEN_CASING[w] ?? (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function actionLabel(action: AuditAction): string {
  return ACTION_LABELS[action] ?? humanizeCode(action);
}

function isPledgeLifecycleAction(action: AuditAction): boolean {
  return action.startsWith('PLEDGE_');
}

// ---------------------------------------------------------------------------
// Details and actor
// ---------------------------------------------------------------------------

type Details = AuditTrailComponents['schemas']['AuditLogEntryResponse']['details'];

function detailString(details: Details | undefined, key: string): string | undefined {
  const value = details?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/**
 * Keys that are request plumbing (or already shown in another column), so
 * they stay out of the one-line summary. They remain in the full record.
 */
const SUMMARY_EXCLUDED_KEYS = new Set([
  'path',
  'method',
  'requestBody',
  'statusCode',
  'durationMs',
  'userAgent',
  'ipAddress',
  'requestId',
  'sessionId',
  'actorEmail',
  'actorRole',
  'actorUserId',
  'tenantId',
  'fiId',
  'filters',
]);

const REASON_KEYS = ['reason', 'declineReason', 'returnReason', 'note', 'notes', 'comment'];

/**
 * Export type and format codes the platform-admin-api export paths record
 * (tenant-export.service.ts, sftp-export.service.ts, examiner-evidence and
 * audit routes). The Details line printed the code uppercased, so a reader saw
 * 'TENANT_PLEDGE_CSV_BUFFER' (r3 fi-g6-2). A code outside this map is
 * humanized, never uppercased.
 */
const EXPORT_TYPE_LABELS: Record<string, string> = {
  csv: 'CSV',
  json: 'JSON',
  pdf: 'PDF',
  tenant_pledge_csv_export: 'CSV download',
  tenant_pledge_csv_buffer: 'CSV for SFTP delivery',
  tenant_pledge_csv_sftp: 'CSV via SFTP',
  tenant_termination_snapshot: 'Termination snapshot',
  examiner_evidence_package: 'Examiner evidence package',
  examiner_evidence_manifest_preview: 'Examiner evidence manifest preview',
};

function exportTypeLabel(code: string): string {
  return EXPORT_TYPE_LABELS[code.trim().toLowerCase()] ?? humanizeCode(code);
}

function exportSummary(details: Details | undefined): string {
  const rowCount = details?.rowCount;
  const format = detailString(details, 'format') ?? detailString(details, 'exportType');
  const since = detailString(details, 'since');
  const until = detailString(details, 'until');
  const parts: string[] = [];
  parts.push(
    typeof rowCount === 'number'
      ? `Exported ${rowCount} ${rowCount === 1 ? 'row' : 'rows'}`
      : 'Export recorded',
  );
  if (format) parts[0] += ` (${exportTypeLabel(format)})`;
  if (since && until) parts.push(`${formatCalendarDate(since)} to ${formatCalendarDate(until)}`);
  else if (since) parts.push(`from ${formatCalendarDate(since)}`);
  else if (until) parts.push(`through ${formatCalendarDate(until)}`);
  return parts.join(', ');
}

/**
 * The one-line Details summary. The full record sits behind the row expander;
 * this line carries only what a reader acts on, and says so plainly when the
 * record carries nothing beyond request metadata.
 */
function summarizeDetails(entry: AuditLogEntry): string {
  const details = entry.details;
  if (entry.action === 'DATA_EXPORT') return exportSummary(details);

  const reason = REASON_KEYS.map((k) => detailString(details, k)).find(Boolean);
  if (reason) return reason;

  const facts = Object.entries(details ?? {})
    .filter(([key, value]) => {
      if (SUMMARY_EXCLUDED_KEYS.has(key) || REASON_KEYS.includes(key)) return false;
      return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
    })
    .map(([key, value]) => `${humanizeCode(key)}: ${String(value)}`);
  return facts.length > 0 ? facts.join('; ') : 'No further details recorded';
}

/**
 * The IP column. Older export records stored the whole forwarded chain
 * ('64.98.193.106,34.117.146.158', client then proxy) under one address
 * column; the first address is the client, the rest are shown as the path it
 * came through, and the full stored value stays in the title and the record
 * (r3 fi-g6-2). '0.0.0.0' is the value the export service writes when no
 * address reached it, so it reads as not recorded rather than as an address.
 */
function ipAddressDisplay(value: string | undefined): {
  client: string | undefined;
  via: string[];
  chain: string;
} {
  const chain = (value ?? '').trim();
  const hops = chain
    .split(',')
    .map((h) => h.trim())
    .filter((h) => h !== '');
  const client = hops[0];
  if (!client || client === '0.0.0.0') return { client: undefined, via: [], chain };
  return { client, via: hops.slice(1), chain };
}

/**
 * The actor as recorded. Some export events carry userId "unknown" while the
 * record's details name the actor's email; the email is shown in that case.
 */
function actorLabel(entry: AuditLogEntry): string {
  const id = entry.userId?.trim();
  if (id && id.toLowerCase() !== 'unknown') return id;
  return detailString(entry.details, 'actorEmail') ?? 'Not recorded';
}

function actorRoleLabel(entry: AuditLogEntry): string | undefined {
  const role = detailString(entry.details, 'actorRole');
  return role ? humanizeCode(role) : undefined;
}

/** The full record for the row expander, without the raw request body. */
function fullRecord(entry: AuditLogEntry): Record<string, unknown> {
  const { requestBody: _requestBody, ...details } = entry.details ?? {};
  return {
    action: entry.action,
    timestamp: entry.timestamp,
    userId: entry.userId,
    resourceType: entry.resourceType,
    resourceId: entry.resourceId ?? null,
    tenantId: entry.tenantId,
    ipAddress: entry.ipAddress,
    userAgent: entry.userAgent,
    requestId: entry.requestId ?? null,
    integrityHash: entry.integrityHash,
    details,
  };
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const COLUMNS: ColumnDef<AuditLogEntry, unknown>[] = [
  {
    accessorKey: 'timestamp',
    header: 'Timestamp',
    cell: ({ getValue }) => (
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-tertiary flex-shrink-0" />
        <span className="text-sm font-mono text-primary tabular-nums leading-normal whitespace-nowrap">
          {formatDateTime(getValue() as string)}
        </span>
      </div>
    ),
    size: 240,
  },
  {
    id: 'actor',
    accessorFn: (row) => actorLabel(row),
    header: 'Actor',
    cell: ({ row }) => {
      const role = actorRoleLabel(row.original);
      return (
        <div>
          <div className="text-sm font-sans font-medium text-primary leading-normal">
            {actorLabel(row.original)}
          </div>
          {role && (
            <div className="text-xs font-sans text-tertiary leading-normal">{role}</div>
          )}
        </div>
      );
    },
    size: 180,
  },
  {
    accessorKey: 'action',
    header: 'Action',
    cell: ({ getValue }) => {
      const action = getValue() as AuditAction;
      return (
        <Badge variant={ACTION_VARIANTS[action] ?? 'neutral'} size="sm">
          {actionLabel(action)}
        </Badge>
      );
    },
    size: 180,
  },
  {
    accessorKey: 'resourceId',
    header: 'Pledge ID',
    cell: ({ row }) => {
      const entry = row.original;
      const id = entry.resourceId;
      // Only a pledge lifecycle event names a pledge. Other events filed under
      // resourceType=pledge (a tenant CSV export) carry a tenant id here.
      if (id && entry.resourceType === 'pledge' && isPledgeLifecycleAction(entry.action)) {
        return (
          <Link
            to={`/pledges/${id}`}
            className="text-sm font-mono text-primary leading-normal truncate max-w-[180px] block hover:underline"
            title={id}
          >
            {id}
          </Link>
        );
      }
      return (
        <span className="text-xs font-sans text-tertiary leading-normal" title="Not a pledge lifecycle event">
          {'—'}
        </span>
      );
    },
    size: 200,
  },
  {
    id: 'details',
    accessorFn: (row) => summarizeDetails(row),
    header: 'Details',
    cell: ({ getValue }) => (
      <span className="text-sm font-sans text-secondary leading-normal line-clamp-2">
        {getValue() as string}
      </span>
    ),
    size: 260,
  },
  {
    accessorKey: 'ipAddress',
    header: 'IP Address',
    cell: ({ getValue }) => {
      const ip = ipAddressDisplay(getValue() as string | undefined);
      if (!ip.client) {
        return (
          <span className="text-xs font-sans text-tertiary leading-normal">Not recorded</span>
        );
      }
      return (
        <div title={ip.chain}>
          <div className="text-sm font-mono text-tertiary tabular-nums leading-normal">
            {ip.client}
          </div>
          {ip.via.length > 0 && (
            <div className="text-xs font-mono text-tertiary tabular-nums leading-normal">
              via {ip.via.join(', ')}
            </div>
          )}
        </div>
      );
    },
    size: 160,
  },
];

// ---------------------------------------------------------------------------
// Filter definitions (client-side over the complete pledge-event set)
// ---------------------------------------------------------------------------

const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'actor',
    label: 'Actor',
    type: 'text',
    placeholder: 'Filter by actor...',
  },
  {
    id: 'action',
    label: 'Action',
    type: 'text',
    placeholder: 'Filter by action...',
  },
];

/** Reads every page of the tenant's pledge events. */
async function fetchAllPledgeEvents(): Promise<{ entries: AuditLogEntry[]; total: number }> {
  const entries: AuditLogEntry[] = [];
  let total = 0;
  let offset = 0;
  for (;;) {
    const page = await auditApi.list({ ...PLEDGE_QUERY, limit: FETCH_BATCH, offset });
    total = page.pagination.total;
    entries.push(...page.data);
    offset += page.data.length;
    if (page.data.length === 0 || offset >= total) break;
  }
  return { entries, total };
}

// ---------------------------------------------------------------------------
// PledgeAuditTrailPage
// ---------------------------------------------------------------------------

/**
 * Pledge Audit Trail page.
 *
 * The three pledge-event counts are derived from the pledge lifecycle events
 * in the complete set the pledge query returns. The tenant-wide audit statistics endpoint counts every audit record,
 * not pledge events, so it feeds only the "Critical audit events" card, which
 * says so.
 */
export function PledgeAuditTrailPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [statistics, setStatistics] = useState<AuditStatistics | null>(null);
  const [statisticsFailed, setStatisticsFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAllPledgeEvents();
      setEntries(result.entries);
      setTotalCount(result.total);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load pledge audit logs';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStatistics = useCallback(async () => {
    try {
      const stats = await auditApi.getStatistics();
      setStatistics(stats);
      setStatisticsFailed(false);
    } catch {
      // Statistics failure is non-blocking; the card says it could not be read.
      setStatistics(null);
      setStatisticsFailed(true);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStatistics();
  }, [fetchLogs, fetchStatistics]);

  // The query pins resourceType=pledge, which also returns records that are
  // not pledge lifecycle events: tenant exports written as resourceType
  // 'pledge' before tenant-export.service moved new exports to 'report'. The
  // three pledge-event tiles count lifecycle events only; the grid still lists
  // the other records, and the total tile says how many there are (fi-g6-1).
  const pledgeCounts = useMemo(() => {
    const lifecycle = entries.filter((e) => isPledgeLifecycleAction(e.action));
    const now = Date.now();
    const within = (ms: number) =>
      lifecycle.filter((e) => {
        const at = Date.parse(e.timestamp);
        return !Number.isNaN(at) && now - at <= ms;
      }).length;
    return {
      total: lifecycle.length,
      other: entries.length - lifecycle.length,
      last24h: within(DAY_MS),
      last7d: within(7 * DAY_MS),
    };
  }, [entries]);

  /**
   * Client-side filtering for actor, action, and free-text search over the
   * complete pledge-event set.
   */
  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      for (const filter of activeFilters) {
        const value = filter.value?.toLowerCase();
        if (!value) continue;
        if (filter.filterId === 'actor' && !actorLabel(entry).toLowerCase().includes(value)) {
          return false;
        }
        if (filter.filterId === 'action') {
          const haystack = `${entry.action} ${actionLabel(entry.action)}`.toLowerCase();
          if (!haystack.includes(value)) return false;
        }
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchable = [
          entry.action,
          actionLabel(entry.action),
          actorLabel(entry),
          entry.resourceId ?? '',
          entry.ipAddress,
          JSON.stringify(entry.details),
        ]
          .join(' ')
          .toLowerCase();
        if (!searchable.includes(query)) return false;
      }
      return true;
    });
  }, [entries, activeFilters, searchQuery]);

  const handleExport = () => {
    const url = auditApi.getExportUrl(PLEDGE_QUERY);
    window.open(url, '_blank');
  };

  const pledgeCountsReady = !error && totalCount !== null;
  const pledgeValue = (n: number): string => (pledgeCountsReady ? String(n) : '--');
  const pledgeCaption = pledgeCountsReady ? undefined : METRIC_READ_CAPTIONS.failed;
  const criticalEvents = statistics?.criticalEvents;

  return (
    <div>
      {/* Page Heading */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">
            Pledge lifecycle events and other audit records filed against
            pledges, by any actor
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              fetchLogs();
              fetchStatistics();
            }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {/* A secondary header action beside Refresh, the neutral treatment
              Active Loans gives its grid export: red is reserved for CTAs and
              alerts (r3 fi-cons-14). */}
          <Button variant="secondary" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            {error}
            <button
              onClick={fetchLogs}
              className="ml-3 font-semibold text-accent hover:underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <MetricCardGrid className="mb-8">
        {loading && totalCount === null && !error ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-surface p-6 rounded-sm shadow-sm border border-recessed"
            >
              <Skeleton className="h-10 w-16 mb-3" />
              <Skeleton className="h-3 w-24" />
            </div>
          ))
        ) : (
          <>
            <MetricCard
              label="Pledge events (24h)"
              value={pledgeValue(pledgeCounts.last24h)}
              caption={pledgeCaption}
            />
            <MetricCard
              label="Pledge events (7d)"
              value={pledgeValue(pledgeCounts.last7d)}
              caption={pledgeCaption}
            />
            <MetricCard
              label="Total pledge events"
              value={pledgeValue(pledgeCounts.total)}
              caption={pledgeCaption}
              description={
                pledgeCountsReady && pledgeCounts.other > 0
                  ? `Lifecycle events only; ${pledgeCounts.other} other ${
                      pledgeCounts.other === 1 ? 'record is' : 'records are'
                    } listed below`
                  : undefined
              }
            />
            <MetricCard
              label="Critical audit events"
              value={typeof criticalEvents === 'number' ? String(criticalEvents) : '--'}
              caption={
                typeof criticalEvents === 'number'
                  ? undefined
                  : statisticsFailed
                    ? METRIC_READ_CAPTIONS.failed
                    : METRIC_READ_CAPTIONS.unread
              }
              description="All audit records, not only pledge events"
              change={typeof criticalEvents === 'number' && criticalEvents > 0 ? 'Requires review' : undefined}
              changeType={typeof criticalEvents === 'number' && criticalEvents > 0 ? 'negative' : 'neutral'}
            />
          </>
        )}
      </MetricCardGrid>

      {/* Filter Bar */}
      <FilterBar
        filters={FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by action, actor, pledge ID, IP..."
        className="rounded-t-sm"
      />

      {/* Data Grid */}
      {loading && !entries.length ? (
        <div className="bg-surface rounded-b-sm shadow-sm p-8">
          <Skeleton className="h-8 w-full mb-4" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </div>
      ) : filteredEntries.length === 0 ? (
        <div className="bg-surface rounded-b-sm shadow-sm p-12 text-center">
          <History className="w-8 h-8 text-tertiary mx-auto mb-3" />
          <p className="text-sm font-sans text-primary font-medium mb-1">
            {error ? 'Pledge audit events could not be loaded' : 'No pledge audit events found'}
          </p>
          <p className="text-xs font-sans text-tertiary">
            {error
              ? 'The pledge events could not be read, so none are shown. Use Retry above.'
              : searchQuery || activeFilters.length > 0
                ? 'Try adjusting your search or filter criteria.'
                : 'Pledge lifecycle events will appear here as consumers interact with collateral pledges.'}
          </p>
        </div>
      ) : (
        <DataGrid<AuditLogEntry>
          data={filteredEntries}
          columns={COLUMNS}
          enablePagination
          defaultPageSize={PAGE_SIZE}
          enableExpanding
          renderSubComponent={({ row }) => (
            <div className="px-4 py-3">
              <p className="text-xs font-sans font-semibold uppercase tracking-wider text-tertiary mb-2">
                Full record
              </p>
              <pre className="text-xs font-mono text-secondary whitespace-pre-wrap break-all">
                {JSON.stringify(fullRecord(row.original), null, 2)}
              </pre>
            </div>
          )}
          enableColumnResize
          hoverable
          striped
          getRowId={(row) => row.id}
          className="rounded-b-sm shadow-sm"
        />
      )}
    </div>
  );
}
